import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Waves } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  // Rolling water bottle animation
  const RollingBottle = () => (
    <motion.div
      initial={{ x: "-100vw", rotate: 0 }}
      animate={{ 
        x: "100vw", 
        rotate: 360 
      }}
      transition={{
        duration: 10,
        repeat: Infinity,
        ease: "linear",
        delay: 2
      }}
      className="absolute top-1/2 w-16 h-16 opacity-20 z-0"
    >
      <div className="w-full h-full bg-gradient-to-b from-transparent via-mava-orange/30 to-transparent rounded-full border-2 border-mava-orange/20" />
      <div className="absolute inset-2 bg-mava-gradient/20 rounded-full" />
    </motion.div>
  );

  return (
    <motion.section
      ref={containerRef}
      style={{ y, opacity }}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-mava-off-white via-white to-mava-off-white"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 water-waves" />
      <div className="absolute inset-0 floating-bubbles" />
      {/* Rolling Bottle */}
      <RollingBottle />
      {/* Main Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        {/* Brand Logo Animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ 
            type: "spring", 
            stiffness: 260, 
            damping: 20,
            delay: 0.2
          }}
          className="mb-8"
        >
          <motion.div
            animate={{ 
              rotate: [0, 5, -5, 0],
              scale: [1, 1.05, 1]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-24 h-24 mx-auto mb-6"
          >
            <Image
              src="/assets/images/mava-1762174454410.jpg"
              alt="Mava Cold Beverages Logo - Premium water bottle brand with leaf accent symbolizing freshness"
              className="w-full h-full object-contain"
            />
          </motion.div>
        </motion.div>

        {/* Brand Name with Leaf */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mb-6"
        >
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-poppins font-bold text-mava-charcoal mb-2">
            ma
            <span className="relative inline-block">
              v
              {/* Leaf on 'v' */}
              <motion.span
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute -top-2 -right-1 text-green-500 text-2xl"
              >
                🍃
              </motion.span>
            </span>
            a
          </h1>
          <p className="text-xl md:text-2xl font-poppins font-medium text-mava-orange -mt-2">
            Cold Beverages
          </p>
        </motion.div>

        {/* Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mb-4"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-poppins font-bold text-mava-charcoal mb-4">
            Sip the freshness, live the moment
          </h2>
          <p className="text-lg md:text-xl font-inter text-gray-600 max-w-2xl mx-auto">
            Mava Cold Beverages — purity redefined.
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
        >
          <Button
            className="bg-mava-orange hover:bg-mava-orange/90 text-white px-8 py-4 text-lg font-poppins font-semibold rounded-full cursor-glow water-ripple brand-transition group"
            onClick={() => document.getElementById('beverages')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <span className="relative z-10">Explore Our Beverages</span>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="ml-2 inline-block"
            >
              <Waves className="w-5 h-5" />
            </motion.div>
          </Button>

          <Button
            variant="outline"
            className="border-2 border-mava-orange text-mava-orange hover:bg-mava-orange hover:text-white px-8 py-4 text-lg font-poppins font-semibold rounded-full cursor-glow brand-transition"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Contact Us
          </Button>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center text-mava-orange cursor-pointer"
            onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
          >
            <span className="text-sm font-inter mb-2 opacity-70">Scroll Down</span>
            <ChevronDown className="w-6 h-6" />
            <motion.div
              animate={{ scaleY: [1, 1.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-px h-8 bg-mava-orange/50 mt-2"
            />
          </motion.div>
        </motion.div>
      </div>
      {/* Decorative Water Waves */}
      <div className="absolute bottom-0 left-0 right-0 h-32 overflow-hidden">
        <motion.div
          animate={{ 
            x: [0, -50, 0],
            scaleY: [1, 1.1, 1]
          }}
          transition={{ 
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-r from-transparent via-mava-gradient to-transparent opacity-20"
          style={{
            clipPath: "polygon(0 100%, 100% 100%, 100% 0, 0 50%)"
          }}
        />
      </div>
    </motion.section>
  );
};

export default HeroSection;